import type { CreateOrderInput, OrderStatus } from '@palihog/shared';
import { supabase } from './supabase';
import type { NotificationRow, OrderRow, Profile, QuotationJobRow, QuoteRow } from './types';
import { UnconfirmedActionError } from '../feedback/notifications';

export async function getProfile(userId: string): Promise<Profile> {
  const { data, error } = await supabase.from('profiles')
    .select('user_id,role,full_name,phone,blocked_at,blocked_reason')
    .eq('user_id', userId)
    .single();
  if (error) throw error;
  return data as Profile;
}

export async function getOrders(): Promise<OrderRow[]> {
  const { data, error } = await supabase.from('orders').select([
    'id', 'public_id', 'customer_id', 'category', 'status', 'pickup_name', 'pickup_address',
    'pickup_source', 'pickup_instructions', 'directory_branch_id',
    'dropoff_address', 'recipient_name', 'recipient_phone', 'parcel_description',
    'delivery_instructions', 'funding_source', 'current_quote_id', 'pricing_rule_id',
    'purchase_budget_centavos', 'estimated_weight_grams', 'package_count', 'package_size', 'submitted_at', 'updated_at',
  ].join(',')).order('submitted_at', { ascending: false });
  if (error) throw error;
  const orders = data as unknown as OrderRow[];
  if (!orders.length) return orders;
  // Fetch job state independently. A broken/missing relationship must fail the
  // whole read instead of being misrepresented to dispatchers as “no job”.
  const { data: jobs, error: jobError } = await supabase.from('quotation_jobs')
    .select('id,order_id,status,attempt_count,max_attempts,available_at,last_error_code,last_error_message,result_quote_id,updated_at,completed_at')
    .in('order_id', orders.map((order) => order.id));
  if (jobError) throw jobError;
  const byOrder = new Map((jobs as QuotationJobRow[]).map((job) => [job.order_id, job]));
  return orders.map((order) => ({ ...order, quotation_jobs: byOrder.has(order.id) ? [byOrder.get(order.id)!] : [] }));
}

export async function getOrder(id: string): Promise<OrderRow> {
  const { data, error } = await supabase.from('orders').select([
    'id', 'public_id', 'customer_id', 'category', 'status', 'pickup_name', 'pickup_address',
    'pickup_source', 'pickup_instructions', 'directory_branch_id',
    'dropoff_address', 'recipient_name', 'recipient_phone', 'parcel_description',
    'delivery_instructions', 'funding_source', 'current_quote_id', 'pricing_rule_id',
    'purchase_budget_centavos', 'estimated_weight_grams', 'package_count', 'package_size', 'submitted_at', 'updated_at',
  ].join(',')).eq('id', id).single();
  if (error) throw error;
  return data as unknown as OrderRow;
}

export interface AssignedRiderIdentity { rider_id: string; display_name: string | null; vehicle_description: string | null; vehicle_plate: string | null; contact_phone: string | null; photo_path: string | null; photo_verified: boolean }
export async function getAssignedRiderIdentity(orderId: string): Promise<AssignedRiderIdentity | null> {
  const { data, error } = await supabase.rpc('customer_assigned_rider_identity', { target_order_id: orderId });
  if (error) throw error;
  if (data === null) return null;
  if (!data || typeof data !== 'object') throw new Error('Assigned rider identity response is invalid.');
  const identity = data as Record<string, unknown>;
  if (typeof identity.rider_id !== 'string' || (identity.contact_phone !== null && typeof identity.contact_phone !== 'string') || (identity.photo_path !== null && typeof identity.photo_path !== 'string') || typeof identity.photo_verified !== 'boolean') throw new Error('Assigned rider identity response is invalid.');
  return data as AssignedRiderIdentity;
}

export async function getQuote(id: string): Promise<QuoteRow> {
  const { data, error } = await supabase.from('quotes')
    .select('*,quote_items(id,description,quantity,unit_price_centavos,kind)')
    .eq('id', id).single();
  if (error) throw error;
  return data as QuoteRow;
}

export async function getQuotationJob(orderId: string): Promise<QuotationJobRow | null> {
  const { data, error } = await supabase.from('quotation_jobs')
    .select('id,order_id,status,attempt_count,max_attempts,available_at,last_error_code,last_error_message,result_quote_id,updated_at,completed_at')
    .eq('order_id', orderId).maybeSingle();
  if (error) throw error;
  return data as QuotationJobRow | null;
}

export async function getNotifications(): Promise<NotificationRow[]> {
  const { data, error } = await supabase.from('notifications')
    .select('id,order_id,kind,title,body,read_at,created_at')
    .order('created_at', { ascending: false }).limit(50);
  if (error) throw error;
  return data as NotificationRow[];
}

export async function createOrder(input: CreateOrderInput): Promise<OrderRow> {
  const { data, error } = await supabase.rpc('submit_order', {
    payload: {
      clientRequestId: input.clientRequestId,
      category: input.serviceCategory,
      pickupSource: input.pickupSource,
      directoryBranchId: input.directoryBranchId,
      savedPickupId: input.savedPickupId,
      recentOrderId: input.recentOrderId,
      pickupName: input.pickupName,
      pickupAddress: input.pickupAddress,
      pickupInstructions: input.pickupInstructions,
      pickup: input.pickupCoordinates,
      pickupConfirmed: input.pickupConfirmed,
      dropoffAddress: input.dropoffAddress,
      dropoff: input.dropoffCoordinates,
      deliveryAddressId: input.deliveryAddressId,
      deliveryConfirmed: input.deliveryConfirmed,
      recipientName: input.recipientName,
      recipientPhone: input.recipientPhone,
      items: input.items,
      parcelDescription: input.parcelDescription,
      deliveryInstructions: input.deliveryInstructions,
      purchaseBudgetCentavos: input.purchaseBudgetCentavos,
      fundingSource: input.fundingSource,
      estimatedWeightGrams: input.estimatedWeightGrams,
      packageCount: input.packageCount,
      packageSize: input.packageSize,
    },
  });
  if (error) throw error;
  if (!data || typeof data.id !== 'string' || !data.id) throw new UnconfirmedActionError();
  return data as OrderRow;
}

export async function decideQuote(id: string, approve: boolean, note?: string, acknowledgedQuoteVersion: number | null = null): Promise<void> {
  const { error } = await supabase.rpc('decide_quote', {
    target_quote_id: id,
    approve,
    decision_note: note ?? null,
    acknowledged_quote_version: acknowledgedQuoteVersion,
  });
  if (error) throw error;
}

export async function transitionOrder(id: string, expected: OrderStatus, next: OrderStatus, reason?: string): Promise<void> {
  const { error } = await supabase.rpc('transition_order', {
    target_order_id: id,
    expected_status: expected,
    next_status: next,
    transition_reason: reason ?? null,
    request_key: crypto.randomUUID(),
  });
  if (error) throw error;
}
